import React, { useState } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Link,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { VimeoEmbed } from "../Utilities/VimeoEmbed";
import { FAQSection } from "./FAQSection";

interface GuideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GuideDrawer = ({ isOpen, onClose }: GuideDrawerProps) => {
  const [isFAQFullView, setIsFAQFullView] = useState(false);
  const drawerBodyRef = React.useRef<HTMLDivElement>(null);

  const drawerContentStyles = {
    maxWidth: "600px",
    width: "100%",
  };

  const scrollToTop = () => {
    if (drawerBodyRef.current) {
      drawerBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (!isOpen) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right">
      <DrawerOverlay />
      <DrawerContent sx={drawerContentStyles}>
        <DrawerCloseButton />
        <DrawerHeader>
          PUSH NOTIFICATIONS & GEO-FENCE NOTIFICATIONS GUIDE
        </DrawerHeader>
        <DrawerBody ref={drawerBodyRef}>
          {!isFAQFullView && (
            <Box>
              <Text fontSize="lg" fontWeight="medium" mb={4}>
                A quick walkthrough and FAQs to help you create and send
                notifications from Cherryhub.
              </Text>
              <Text fontSize="md" fontWeight="bold" mb={4}>
                How Push Notifications Work
              </Text>
              <Text fontSize="lg" fontWeight="medium" mb={4}>
                Watch this quick demo to see how to create, preview, and send a
                push notification.
              </Text>
              <VimeoEmbed
                videoId="1209363718"
                title="Push Notifications & Geo-fence Notifications Guide"
              />

              <Box mt={6}>
                <Text fontSize="md" fontWeight="bold" mb={4}>
                  Quick Steps
                </Text>
                <OrderedList spacing={2} pl={4}>
                  <ListItem>
                    Create a Notification
                    <UnorderedList pl={4} mt={1}>
                      <ListItem>
                        Enter a notification name (for internal use only).
                      </ListItem>
                    </UnorderedList>
                  </ListItem>
                  <ListItem>
                    Configure Your Messages
                    <UnorderedList pl={4} mt={1}>
                      <ListItem>
                        Create a Push Notification message (optional).
                      </ListItem>
                      <ListItem>
                        Configure Geo-fence location messages (optional).
                      </ListItem>
                      <ListItem>
                        At least one message (Push Notification or Geo-fence) is
                        required before the campaign can be saved.
                      </ListItem>
                      <ListItem>
                        Must follow{" "}
                        <Link
                          href="https://developer.apple.com/support/terms/apple-developer-program-license-agreement/#A1"
                          color="blue.500"
                          textDecoration="underline"
                          isExternal
                        >
                          Apple
                        </Link>{" "}
                        and{" "}
                        <Link
                          href="https://developers.google.com/wallet/generic/use-cases/trigger-push-notifications#some-considerations-when-sending-messages-with-notifications-to-users"
                          color="blue.500"
                          textDecoration="underline"
                          isExternal
                        >
                          Google
                        </Link>{" "}
                        Wallet guidelines.
                      </ListItem>
                    </UnorderedList>
                  </ListItem>
                  <ListItem>
                    Select Your Audience
                    <UnorderedList pl={4} mt={1}>
                      <ListItem>
                        Apply Manual Filters or use a Saved Segment to define
                        eligible members.
                      </ListItem>
                    </UnorderedList>
                  </ListItem>
                  <ListItem>
                    Schedule Your Campaign
                    <UnorderedList pl={4} mt={1}>
                      <ListItem>Select the send date and time.</ListItem>
                      <ListItem>
                        Configure an optional Stop Sending After date.
                      </ListItem>
                      <ListItem>
                        If using Geo-fence campaign message overrides, specify
                        the campaign end date so overridden messages revert to
                        their default location messages.
                      </ListItem>
                    </UnorderedList>
                  </ListItem>
                  <ListItem>
                    Review Your Campaign
                    <UnorderedList pl={4} mt={1}>
                      <ListItem>
                        Preview the Push Notification (Apple & Google Wallet).
                      </ListItem>
                      <ListItem>
                        Review Geo-fence location messages and campaign summary.
                      </ListItem>
                    </UnorderedList>
                  </ListItem>
                  <ListItem>
                    Schedule or Save as Draft
                    <UnorderedList pl={4} mt={1}>
                      <ListItem>
                        Schedule the campaign immediately or save it as a draft
                        to complete later.
                      </ListItem>
                    </UnorderedList>
                  </ListItem>
                </OrderedList>
              </Box>
            </Box>
          )}

          <FAQSection
            isFullView={isFAQFullView}
            onViewAllClick={() => {
              setIsFAQFullView(true);
              scrollToTop();
            }}
            onBackClick={() => setIsFAQFullView(false)}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
